<template>
  <PokerTableSkeleton
    class="trainer-table traintabl"
    :players="players"
    :heroPosition="l_heroPosition"
    :tableDirection="tableDirection"
    :rotateHero="true"
    :tstPrefix="tstPrefix"
    @touchstart.stop="swpHandleTouchStart"
    @touchmove.stop="swpHandleTouchMove"
    @touchend.stop="swpHandleTouchEnd"
  >
    <template #seats_before>
      <TrainerSessionCounter
        v-if="hasSessionCount"
        class="traintabl_session-counter"
        :sessionHand="sessionCount.current_hands"
        :sessionMaxHands="sessionCount.max_hands"
      />

      <TrainerTableControls
        v-if="hasTableControls"
        class="traintabl_controls"
        :namespace="namespace"
      />

      <TrainerRng
        v-if="hasRng"
        class="traintabl_rng"
        :rng="rng"
        :mode="rngMode"
        :data-tst="`${tstPrefix}_trainer_rng`"
      />
      <TrainerStreak
        v-if="hasStreak"
        :streak="streak"
        class="traintabl_streak"
        :data-tst="`${tstPrefix}_trainer_streak`"
      />
      <TrainerHintsFloatOpener
        v-if="hasHintsMenu"
        :namespace="namespace"
        class="traintabl_hints"
      />
      <TrainerStudySpotBtn
        v-if="hasStudySpotBtn"
        :namespace="namespace"
        class="traintabl_study-btn"
      />
    </template>

    <template #any>
      <TrainerResultTile
        v-if="hasQuickResult"
        class="traintabl_quickres"
        :tstPrefix="tstPrefix"
        :show="showQuickResult"
        :hasFrequency="true"
        :action="action"
      />
    </template>

    <template #center>
      <TrainerStandoff
        v-if="hasStandoff || hasDepthText"
        class="traintabl_standoff"
        style="--weight: 500; --size: 1em; --letter-spacing: 0px; --justify-content: center"
        :queryStandoff="queryStandoff"
        :onlyDepth="hasDepthText"
        :gameformat="gameformat"
        :customTree="customTree"
      />
      <PokerPot
        :spot="spot"
        :useNext="useNextPot"
        class="traintabl_pot"
      >
        <PokerBoard
          :spot="spot"
          :useNext="useNextBoard"
          :hasSounds="hasSounds"
        />
      </PokerPot>
      <div
        v-if="tableFinished"
        class="anim-fade-in traintabl_message"
      >
        <div class="traintabl_message_content text-center">
          <div class="text-normal weight_500">
            {{ $t('messages.trainer_table_finished') }}
          </div>
          <div class="text-small clr-grey">
            {{ $t('messages.trainer_table_finished_sub') }}
          </div>
        </div>
      </div>
    </template>
    <template
      v-for="player in players"
      #[player.position]
      :key="player.position"
    >
      <PokerPlayer
        class="traintabl_player"
        :player="player"
        :players_props="l_players_props"
        :useNext="useNextPlayer"
        :hasStack="hasStack"
        :betInPot="betInPot"
        :hasSounds="hasSounds"
        @timebank:stop="$emit('timebank:stop')"
      />
    </template>
  </PokerTableSkeleton>
</template>

<script>
import PokerTableSkeleton from '@/components/PokerTable/PokerTableSkeleton'
import PokerPlayer from '@/components/PokerTable/PokerPlayer'
import PokerPot from '@/components/PokerTable/PokerPot'
import PokerBoard from '@/components/PokerTable/PokerBoard'
import TrainerRng from '@/components/Trainer/TrainerRng'
import TrainerResultTile from '@/components/Trainer/TrainerResultTile'
import TrainerHintsFloatOpener from '@/components/Trainer/TrainerHintsFloatOpener'
import TrainerStandoff from '@/components/Trainer/TrainerStandoff'
import TrainerStreak from '@/components/Trainer/TrainerStreak'
import TrainerSessionCounter from '@/components/Trainer/TrainerSessionCounter'
import TrainerTableControls from '@/components/Trainer/TrainerTableControls'
import TrainerStudySpotBtn from '@/components/Trainer/TrainerStudySpotBtn'
import swipe_mix from '@/mixins/swipe_mix'
import format_mix from '@/mixins/format_mix'
import { getPlayersDefault, getLastSpotsOpponent } from '@/store/gamestate/gamestate'

export default {
  name: 'PokerTableTrainer',

  components: {
    PokerPlayer,
    PokerPot,
    PokerBoard,
    PokerTableSkeleton,
    TrainerSessionCounter,
    TrainerRng,
    TrainerStreak,
    TrainerResultTile,
    TrainerHintsFloatOpener,
    TrainerStandoff,
    TrainerTableControls,
    TrainerStudySpotBtn,
  },
  mixins: [swipe_mix, format_mix],

  props: {
    namespace: { String, required: true },
    spot: Object,
    querySetup: Object,
    queryStandoff: Object,
    queryGameformat: Object,
    gameformat: Object,
    customTree: Object,
    tableDirection: String,
    queryHandTmp: String,
    queryHandOpTmp: String,

    useNextPot: Boolean,
    useNextBoard: Boolean,
    useNextPlayer: Boolean,

    betInPot: Boolean,
    heroPosition: String,
    rng: [Number, String],
    hasSessionCount: Boolean,
    sessionCount: Object,
    hasHintsMenu: Boolean,
    hasStudySpotBtn: Boolean,
    streak: Object,
    hasTableControls: Boolean,
    hasStreak: { type: Boolean, default: true },
    hasStandoff: { type: Boolean, default: true },
    hasRng: { type: Boolean, default: true },
    hasDepthText: Boolean,
    rngMode: String,
    action: Object,
    hasQuickResult: Boolean,
    showQuickResult: Boolean,
    hasSounds: Boolean,
    tableFinished: Boolean,
    opponentHandOnlyOnShowdown: Boolean,
    tstPrefix: { default: 'ptabl' },
  },
  emits: ['timebank:stop', 'swipe:left', 'swipe:right'],
  data() {
    return {
      l_heroPosition: '',
    }
  },
  methods: {
    swpLeft() {
      this.$emit('swipe:left')
    },
    swpRight() {
      this.$emit('swipe:right')
    },
    getDistanceFromSB(position) {
      if (position === 'SB') return 0
      const posIdx = this.l_positions.findIndex(i => i === position)
      let res = 0
      if (posIdx > this.indexSB) {
        res = posIdx - this.indexSB
      } else {
        res = this.l_positions.length - this.indexSB + posIdx
      }
      return res
    },
  },
  computed: {
    hasStack() {
      return !!this.spot?.players
    },
    showdownOpponent() {
      const res = getLastSpotsOpponent({
        history_spots: this.$store.getters[`${this.namespace}/history_spots`],
        gameformat: this.gameformat,
      })
      return res
    },
    players() {
      let res =
        this.spot?.players ||
        getPlayersDefault({ customTree: this.customTree, gameformat: this.gameformat })

      const heHand = this.queryHandTmp
      let opHand = this.queryHandOpTmp

      const hePosition = this.heroPosition
      const activeOpponents = res.filter(i => (i.is_hero ? false : !i.is_folded))
      const oneOnOne = activeOpponents.length <= 1
      let opPosition = oneOnOne ? activeOpponents[0]?.position || null : null

      if (this.spot?.type === `showdown` && !opPosition && oneOnOne) {
        opPosition = this.showdownOpponent?.position || null
      }
      if (this.opponentHandOnlyOnShowdown && this.spot?.type !== `showdown`) {
        opHand = ''
      }

      res = res.map(p => ({
        ...p,
        is_active:
          hePosition === p.position ||
          (oneOnOne
            ? opPosition === p.position
            : this.spot?.type === `showdown`
              ? false
              : hePosition
                ? p.is_active
                : false),
        is_hero: p.position === hePosition,
        is_bot: hePosition !== p.position,
      }))
      res = res.map(p => {
        let r = { ...p, hand: null }
        if (hePosition === p.position && heHand) {
          r = {
            ...r,
            hand: heHand,
          }
        }
        if (opPosition === p.position && opHand) {
          r = {
            ...r,
            hand: opHand,
          }
        }
        return r
      })

      return res
    },
    dev_players() {
      // let stacks = [999.5, 773.5, 423.25]
      // let stacks = [99.5, 0.66, 333.5, 555.5, 11.25, 777.44]
      const stacks = [200]
      // let stacks = [999]
      // const res = [...new Array(2).keys()]
      // const res = [...new Array(3).keys()]
      // const res = [...new Array(4).keys()]
      // const res = [...new Array(5).keys()]
      // const res = [...new Array(6).keys()]
      // const res = [...new Array(7).keys()]
      const res = [...new Array(8).keys()]
        // const res = [...new Array(9).keys()]
        .map((i, idx) => {
          const stack = stacks[Math.floor(stacks.length * Math.random())] || stacks[0]
          const r = {
            // position: 'UTG',
            // hand: idx === 99 ? 'EeEe' : 'AsAs',
            stack,
            position: `UTG${idx}`,
            hand: 'AsAs',
            chips_on_table: stack,
            current_stack: stack,
            next_chips_on_table: stack,
            next_current_stack: stack,
            is_dealer: true,
            is_folded: false,
            next_is_folded: false,
            is_hero: false,
            is_active: true,
            // halo: true,
          }
          return r
        })
      return res
    },
    l_positions() {
      const res = this.players.map(i => i.position)
      return res
    },
    indexSB() {
      const res = this.l_positions.findIndex(i => i === 'SB')
      return res
    },
    l_players_props() {
      const player_animation = this.$store.getters[`${this.namespace}/player_animation`]
      const players_props = this.$store.getters[`${this.namespace}/players_props`]
      let playerWaiting = null
      if (player_animation?.position) {
        playerWaiting = {
          [player_animation.position]: {
            is_bot: player_animation.is_bot,
            halo: player_animation.halo,
            timebank: player_animation.timebank,
            ...(players_props[player_animation.position] || {}),
          },
        }
      }
      const props = {
        ...players_props,
        ...(playerWaiting || {}),
      }
      return props
    },
    depthText() {
      if (this.gameformat?.depth) {
        const depth = `${this.numRoundTo5bb(Number(this.gameformat?.depth || 0))}BB`
        return depth
      }
      const stacks = `${this.gameformat?.stacks
        .split('-')
        .map(n => this.numRoundTo5bb(Number(n)))
        .join('-')}BB`
      return stacks
    },
  },
  watch: {
    heroPosition(position) {
      this.l_heroPosition = position || this.l_heroPosition
    },
  },
}
</script>
<style lang="scss" scoped>
.traintabl_top_right {
  top: 0;
  left: 100%;
  position: absolute;
  transform: translate(-50%, -50%);
}
.traintabl_top_left {
  top: 0;
  left: 0;
  position: absolute;
  transform: translate(-50%, -50%);
}
.traintabl_bottom_right {
  bottom: 0;
  left: 100%;
  position: absolute;
  transform: translate(-50%, 50%);
}
.traintabl_bottom_left {
  bottom: 0;
  left: 0;
  position: absolute;
  transform: translate(-50%, 50%);
}
.traintabl {
  position: relative;
  --standoff-top: -0.75rem;
  &_message {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    &_content {
      background: var(--clr-black-1);
      border-radius: 8px;
      padding: 1rem;
    }
  }
  &_rng {
    @extend .traintabl_bottom_left;
    z-index: 5;
  }
  &_controls {
    @extend .traintabl_top_left;
    position: absolute !important;
    z-index: 5;
  }
  &_session-counter {
    @extend .traintabl_top_right;
    z-index: 5;
  }
  &_streak {
    @extend .traintabl_top_left;
    z-index: 5;
  }
  &_study-btn {
    @extend .traintabl_top_right;
    z-index: 10;
  }
  &_hints {
    @extend .traintabl_top_right;
    z-index: 10;
  }
  &_quickres {
    width: 100%;
    position: absolute;
    right: 0;
    bottom: 4.85em;
    z-index: 4;
  }
  &_standoff {
    color: var(--clr-black-6);
    min-height: 0;
    height: 0;
    position: relative;
    top: var(--standoff-top);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

@media (max-width: 1023px) {
  .traintabl {
    &_streak {
      left: -2.5rem;
      top: -2.5rem;
      transform: translate(0, 0);
    }
    &_session-counter {
      position: absolute;
      right: -2.25rem;
      top: -2.5rem;
      left: unset;
      transform: translate(0, 0);
    }
    &_rng {
      position: absolute;
      left: -2.5rem;
      bottom: 0;
      transform: translate(0, 0);
    }
    &_quickres {
      bottom: 28.3%;
    }
  }
}
</style>
